import type { CoreProductEntity } from '@ts/core/src/modules/coreproduct/coreproduct.entity'
import { ProductType } from '@ts/core/src/modules/coreproduct/coreproduct.types'
import { formatUrlSegment } from './formatUrlSegment'

type BuildProductUrlOptions = {
  bundleGroup?: string
  pricelist?: string
}

const appendQuery = (path: string, key: string, value?: string) => {
  if (!value) return path
  const sep = path.includes('?') ? '&' : '?'
  return `${path}${sep}${key}=${encodeURIComponent(value)}`
}

export const getCanonicalProductGroupSegment = (product: CoreProductEntity): string => {
  const isBundle = product?.type === ProductType.BUNDLE

  if (isBundle) {
    // Bundles currently use the shorter /product/<brand>/<slug> shape
    return formatUrlSegment(product?.productGroup?.brand?.name ?? '')
  }

  // Standard canonical shape: /product/<product-name-slug>/<lowercase-id>
  return (
    formatUrlSegment(product?.productGroup?.brand?.name ?? '') +
    '-' +
    formatUrlSegment(product?.productGroup?.name ?? '') +
    '-' +
    formatUrlSegment(product?.factorDescription ?? '')
  )
}

export const getCanonicalProductPath = (
  product: CoreProductEntity,
  options: BuildProductUrlOptions = {},
): string => {
  const group = getCanonicalProductGroupSegment(product)
  const slug = formatUrlSegment(product?.slug ?? '')

  let path = `/product/${group}/${slug}`

  path = appendQuery(path, 'bundleGroup', options.bundleGroup)
  path = appendQuery(path, 'pricelist', options.pricelist)

  return path
}

export const getProductLinkProps = (
  product: CoreProductEntity,
  options: BuildProductUrlOptions = {},
): { href: string; hrefAs: string } => {
  let href = '/product/[group]/[slug]'

  if (options.bundleGroup) {
    href = appendQuery(href, 'bundleGroup', options.bundleGroup)
  }

  if (options.pricelist) {
    href = appendQuery(href, 'pricelist', options.pricelist)
  }

  return {
    href,
    hrefAs: getCanonicalProductPath(product, options),
  }
}

export const getProductReviewLinkProps = (
  product: CoreProductEntity,
): { href: string; hrefAs: string } => {
  return {
    href: '/product/[group]/[slug]/review',
    hrefAs: `${getCanonicalProductPath(product)}/review`,
  }
}
